import { type FC, useCallback, useRef, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading } from "@stubhub/broadway-web";
import type { CategoryGridItem } from "src/http/types";
import { Content } from "src/shared/components/content";
import type { ContentId } from "src/shared/components/content/types";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { useCarouselAnalytics } from "src/website-apps/shared/components/carousel-module/use-carousel-analytics";
import { CategoryEntryGrid } from "src/website-apps/shared/components/category-entry-grid/CategoryEntryGrid";
import { ClickableLocationLink } from "src/website-apps/shared/components/events-grid/events-grid-title/ClickableLocationLink";
import { CarouselTitle } from "src/website-apps/shared/components/GenericCategoryCarousel.styled";
import { AlternativePerformerEntry } from "./AlternativePerformerEntry";
import { type PerformerGridVariant, gridWrapper, performerGridRecipe } from "./styled-components";

interface Props {
  items: CategoryGridItem[];
  titleContentId?: ContentId | undefined;
  variant?: PerformerGridVariant | undefined;
  categoryId?: number | undefined;
}

export const AlternativePerformerNewGrid: FC<Props> = ({
  items,
  titleContentId = "cid$#1812269#", // "Popular artists near you"
  variant = "default",
  categoryId,
}) => {
  const isCompact = variant === "compact";
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const gridStyles = performerGridRecipe({ variant });

  const [pagedItems, setPagedItems] = useState(isMobile ? items.slice(0, 3) : items);
  const hasMore = isMobile ? items.length !== pagedItems.length : false;
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const { carouselInViewRef, fireItemClick, fireItemHover } = useCarouselAnalytics({
    scrollContainerRef: gridContainerRef,
    itemCount: items.length,
    carouselId: CarouselId.AlternativePerformers,
    itemType: CarouselItemType.Artist,
    categoryId,
    trackSwipe: false,
  });
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      gridContainerRef.current = node;
      carouselInViewRef(node);
    },
    [carouselInViewRef],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div ref={setContainerRef} className={gridStyles.container}>
      <CarouselTitle as="div" className={gridStyles.title}>
        <Heading as="h2">
          <Content id={titleContentId} />
          {isCompact && <ClickableLocationLink />}
        </Heading>
      </CarouselTitle>
      {isCompact ? (
        <div className={gridStyles.grid}>
          {items.slice(0, 4).map((item, index) => (
            <AlternativePerformerEntry
              key={item.categoryId ?? index}
              categoryItem={item}
              itemIndex={index}
              itemCount={items.length}
              variant={variant}
              categoryId={categoryId}
              onAnalyticsClick={() => {
                if (item.categoryId !== undefined) {
                  fireItemClick(item.categoryId, index);
                }
              }}
              onAnalyticsHover={() => {
                if (item.categoryId !== undefined) {
                  fireItemHover(item.categoryId, index);
                }
              }}
            />
          ))}
        </div>
      ) : (
        <div className={gridWrapper}>
          <CategoryEntryGrid
            items={pagedItems}
            onLoadMore={() => {
              setPagedItems(items);
            }}
            hasMore={hasMore}
            fullWidthSeeMore
            renderEntry={(item, index) => (
              <AlternativePerformerEntry
                key={item.categoryId ?? index}
                categoryItem={item}
                itemIndex={index}
                itemCount={items.length}
                variant={variant}
                categoryId={categoryId}
                onAnalyticsClick={() => {
                  if (item.categoryId !== undefined) {
                    fireItemClick(item.categoryId, index);
                  }
                }}
                onAnalyticsHover={() => {
                  if (item.categoryId !== undefined) {
                    fireItemHover(item.categoryId, index);
                  }
                }}
              />
            )}
          />
        </div>
      )}
    </div>
  );
};
